import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Work as BureauIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import StatCards from '../../common/StatCards';
import TableList, { TableRowActions } from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { BUREAU_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import FeedbackMessage from '../../common/FeedbackMessage';
import { glassCardSx } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { motion } from 'framer-motion';

const BureauList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  const MotionCard = motion(Card);
  const NEON3 = '#34D399';
  const SURFACE_MID  = '#0a1628';

  const [bureaus, setBureaus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBureau, setSelectedBureau] = useState(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const filterOptions = [
    {
      name: 'description',
      label: 'Description Status',
      type: 'select',
      options: [
        { value: 'has', label: 'Has Description' },
        { value: 'none', label: 'No Description' }
      ]
    }
  ];

  useEffect(() => {
    fetchBureaus({ forceRefresh: Boolean(location.state?.refreshList) });
  }, [location.pathname, location.state?.refreshList]);

  const fetchBureaus = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/bureaus', forceRefresh ? { cache: false } : {});
      setBureaus(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching bureaus:', error);
      setError('Failed to load bureaus. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bureau) => {
    setSelectedBureau(bureau);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedBureau) return;
    try {
      setConfirmDeleteLoading(true);
      await api.delete(`/bureaus/${selectedBureau.id}`);
      setBureaus(bureaus.filter(b => b.id !== selectedBureau.id));
      setSuccess('Bureau deleted successfully');
    } catch (error) {
      console.error('Error deleting bureau:', error);
      setError('Failed to delete bureau. Please try again.');
    } finally {
      setConfirmDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedBureau(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const nextActive = newFilters.description === 'has' ? 'has' : newFilters.description === 'none' ? 'none' : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'has', 'none'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.description;
    } else {
      nextFilters.description = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'has' ? 1 :
    activeStatFilter === 'none' ? 2 :
    0;

  const filteredBureaus = bureaus.filter(bureau => {
    const matchesSearch = bureau.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (bureau.description && bureau.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (key === 'description') {
        if (value === 'has') return bureau.description;
        if (value === 'none') return !bureau.description;
      }
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: BureauIcon, value: bureaus.length, label: 'Total Bureaus', color: 'info' },
    { icon: DescriptionIcon, value: bureaus.filter(b => b.description).length, label: 'With Description', color: 'success' },
    { icon: DescriptionIcon, value: bureaus.filter(b => !b.description).length, label: 'No Description', color: 'warning' },
  ];

  // Define columns for TableList
  const columns = [
    {
      key: 'name',
      label: 'Name',
      icon: BureauIcon,
      minWidth: 200,
      render: (bureau) => (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {bureau.name}
        </Typography>
      )
    },
    {
      key: 'description',
      label: 'Description',
      minWidth: 300,
      render: (bureau) => (
        <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9) }}>
          {bureau.description || (
            <span style={{ color: theme.palette.text.disabled }}>-</span>
          )}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 140,
      render: (bureau) => (
        <TableRowActions
          viewTitle="View Bureau"
          editTitle="Edit Bureau"
          deleteTitle="Delete Bureau"
          onView={(e) => { e.stopPropagation(); navigate(`/bureaus/${bureau.id}`); }}
          onEdit={(e) => { e.stopPropagation(); navigate(`/bureaus/edit/${bureau.id}`); }}
          onDelete={(e) => { e.stopPropagation(); handleDelete(bureau); }}
        />
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Loading message="Loading bureaus list" size={56} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Header
        title="Bureau"
        subtitle="Manage organization bureaus and divisions"
        icon={BureauIcon}
        actionButton={
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/bureaus/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              alignSelf: { xs: 'stretch', sm: 'auto' }
            }}
          >
            Add Bureau
          </Button>
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
          border: `1px solid ${alpha(NEON3, 0.35)}`,
          boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
          color: alpha(NEON3, 0.95),
          mb: 3,
        }}
      />

      {/* Stat Cards */}
      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      {/* Enhanced Filter Bar */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...glassCardSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by bureau name or description..."
          />
        </CardContent>
      </MotionCard>

      <ListBulkToolbar
        config={BUREAU_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: BUREAU_BULK_CONFIG.endpoint,
          setItems: setBureaus,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'bureau(s)',
          refetch: fetchBureaus,
          setError
        })}
      />

      {/* Bureaus Table */}
      <TableList
        columns={columns}
        data={filteredBureaus}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={loading}
        {...bulkTableProps(BUREAU_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
        onRowClick={(bureau) => navigate(`/bureaus/${bureau.id}`)}
        emptyState={{
          icon: <BureauIcon />,
          title: 'No bureaus found',
          description: searchTerm || Object.keys(filters).length > 0
            ? "Try adjusting your filters to see more bureaus."
            : "Get started by adding your first bureau."
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedBureau(null);
        }}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        itemType="bureau"
        itemName={selectedBureau?.name || ''}
        description="This action cannot be undone and will permanently remove all bureau data."
        confirmLabel="Delete Bureau"
        loading={confirmDeleteLoading}
      />
    </Container>
  );
};

export default BureauList;